import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, ChevronDown, Clock, Copy, Globe, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const CHIP = {
  pending: ["bg-amber-50 text-amber-700 border-amber-200", Clock],
  checking: ["bg-blue-50 text-blue-700 border-blue-200", Loader2],
  provisioning: ["bg-blue-50 text-blue-700 border-blue-200", ShieldCheck],
  live: ["bg-emerald-50 text-emerald-700 border-emerald-200", CheckCircle2],
  broken: ["bg-red-50 text-red-700 border-red-200", AlertTriangle],
};

export const StatusChip = ({ status, label, testid }) => {
  const [cls, Icon] = CHIP[status] || CHIP.pending;
  return <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${cls}`}><Icon size={12} className={status === "checking" ? "animate-spin" : ""} /> {label || status}</span>;
};

const CopyRow = ({ r, i }) => (
  <div data-testid={`dns-row-${i}`} className="grid grid-cols-[52px_1fr_auto] items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
    <span className="font-mono text-[10px] font-bold text-slate-500">{r.type}</span>
    <div className="min-w-0">
      <div className="font-mono text-xs truncate"><span className="text-slate-400">nome</span> {r.name} <span className="text-slate-300">·</span> <span className="text-slate-400">valor</span> {r.value}</div>
      <div className="text-[11px] text-slate-400">{r.hint}</div>
    </div>
    <button data-testid={`dns-copy-${i}`} onClick={() => { navigator.clipboard.writeText(r.value); toast.success("Valor copiado"); }} className="h-7 w-7 grid place-items-center rounded-lg hover:bg-slate-100 text-slate-500"><Copy size={13} /></button>
  </div>
);

export const DomainWizard = ({ projectId, onBound }) => {
  const [cfg, setCfg] = useState(null);
  const [items, setItems] = useState([]);
  const [domain, setDomain] = useState("");
  const [withWww, setWithWww] = useState(true);
  const [path, setPath] = useState("");
  const [adv, setAdv] = useState(false);
  const [pre, setPre] = useState(null);
  const [busy, setBusy] = useState("");
  const [current, setCurrent] = useState(null);

  const load = async () => {
    const { data } = await api.get("/domains");
    setCfg(data.config); setItems(data.items.filter((d) => !d.www_of));
    const mine = data.items.find((d) => d.project_id === projectId && !d.www_of);
    if (mine) setCurrent(mine);
  };
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const check = async () => {
    setBusy("pre");
    try { const { data } = await api.post("/domains/precheck", { domain }); setPre(data); }
    catch (e) { toast.error(errMsg(e)); setPre(null); } finally { setBusy(""); }
  };
  const add = async () => {
    setBusy("add");
    try {
      const { data } = await api.post("/domains", { domain, project_id: projectId, path, with_www: withWww });
      setCurrent(data.domain); setDomain(""); setPre(null); await load(); onBound?.(data.domain);
      toast.success("Domínio adicionado. Crie os registros e clique em Verificar.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const verify = async (d) => {
    setBusy("verify");
    try {
      const { data } = await api.post(`/domains/${d.id}/verify`);
      setCurrent(data);
      data.status === "live" ? toast.success("Domínio no ar!") : toast.error(data.error || "Ainda não. Confira os registros e tente de novo.");
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const reuse = async (d) => {
    try { const { data } = await api.patch(`/domains/${d.id}`, { project_id: projectId, path: d.path || "" }); setCurrent(data); onBound?.(data); toast.success(`${d.domain} agora aponta para esta página`); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!cfg) return <div data-testid="domain-wizard-loading" className="h-24 grid place-items-center"><Loader2 size={18} className="animate-spin text-slate-300" /></div>;

  if (!cfg.enabled) return (
    <div data-testid="domain-soon" className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 text-sm">
      <div className="font-semibold text-[var(--ink)] flex items-center gap-2"><Clock size={15} /> Domínio próprio: disponível em breve</div>
      <p className="mt-2 text-slate-600">Para ligar essa opção faltam três coisas do lado da conta:</p>
      <ol className="mt-2 list-decimal pl-5 space-y-1 text-slate-600">
        <li>Um domínio do próprio Lowpages como destino fixo (ex.: <code className="font-mono">app.lowpages.dev</code>) ligado à aplicação publicada.</li>
        <li>Conta na borda TLS (Approximated) e a chave de API — cerca de US$ 20/mês cobrindo 100 domínios de clientes.</li>
        <li>Um segredo compartilhado entre a borda e a aplicação.</li>
      </ol>
      <p className="mt-2 text-slate-500 text-xs">Enquanto isso, publique pelo endereço do Lowpages — ele já tem SSL e funciona em qualquer canal.</p>
    </div>
  );

  const d = current;
  return (
    <div className="space-y-4" data-testid="domain-wizard">
      {d ? (
        <div className="rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0"><div className="font-display font-semibold flex items-center gap-2 truncate"><Globe size={15} className="text-slate-400" /> {d.domain}{d.path}</div>
              <div className="mt-1 text-xs text-slate-500">Última checagem: {d.last_check ? new Date(d.last_check).toLocaleString("pt-BR") : "ainda não"} · certificado: {d.cert_status || "—"}</div></div>
            <StatusChip status={d.status} label={d.status_label} testid="domain-status-chip" />
          </div>
          <div className="mt-3 space-y-2">{d.records.map((r, i) => <CopyRow key={i} r={r} i={i} />)}</div>
          <div className="mt-3 flex items-center gap-2">
            <button data-testid="domain-verify-now" disabled={busy === "verify"} onClick={() => verify(d)} className="btn btn-ink !py-2 !px-3 text-xs shrink-0 whitespace-nowrap disabled:opacity-60"><RefreshCw size={13} className={busy === "verify" ? "animate-spin" : ""} /> Verificar agora</button>
            <span className="text-[11px] text-slate-400 min-w-0">Reverificamos sozinhos nas primeiras horas e te avisamos por e-mail quando entrar no ar.</span>
          </div>
        </div>
      ) : (
        <>
          {items.length > 0 && (
            <div data-testid="domain-reuse-list" className="rounded-2xl border border-slate-200 p-3">
              <div className="text-xs font-semibold text-slate-500 mb-2">Usar um domínio que já está na sua conta</div>
              <div className="flex flex-wrap gap-2">{items.map((i) => <button key={i.id} data-testid={`domain-reuse-${i.id}`} onClick={() => reuse(i)} className="h-8 px-3 rounded-lg border border-slate-200 text-xs hover:border-[var(--blue)] flex items-center gap-1.5"><Globe size={12} /> {i.domain} <span className="text-slate-400">{i.status_label}</span></button>)}</div>
            </div>
          )}
          <div className="flex gap-2">
            <input data-testid="wizard-domain-input" value={domain} onChange={(e) => { setDomain(e.target.value); setPre(null); }} placeholder="clinicax.com.br" className="h-10 flex-1 rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-[var(--blue)]" />
            <button data-testid="wizard-precheck" disabled={!domain || busy === "pre"} onClick={check} className="btn btn-ghost !py-2 !px-3 text-xs disabled:opacity-50">{busy === "pre" ? <Loader2 size={13} className="animate-spin" /> : "Checar domínio"}</button>
          </div>
          {pre && (
            <div data-testid="precheck-result" className={`rounded-xl border p-3 text-xs ${pre.situation === "ours" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : pre.situation === "taken" ? "border-red-200 bg-red-50 text-red-700" : pre.situation === "other_service" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
              {pre.message}
            </div>
          )}
          {pre && pre.situation !== "taken" && (
            <>
              {pre.is_apex && <label className="flex items-center gap-2 text-xs text-slate-600"><input data-testid="wizard-www" type="checkbox" checked={withWww} onChange={(e) => setWithWww(e.target.checked)} /> conectar <code className="font-mono">www.{pre.domain}</code> também (recomendado)</label>}
              <button data-testid="wizard-adv-toggle" onClick={() => setAdv(!adv)} className="text-xs text-slate-500 flex items-center gap-1 hover:text-[var(--ink)]">opções avançadas <ChevronDown size={12} className={adv ? "rotate-180" : ""} /></button>
              {adv && (
                <label className="block text-xs text-slate-600">Servir em um caminho do domínio (opcional)
                  <input data-testid="wizard-path" value={path} onChange={(e) => setPath(e.target.value)} placeholder="/promo" className="mt-1 h-9 w-full rounded-xl border border-slate-200 px-3 text-sm font-mono outline-none focus:border-[var(--blue)]" />
                  <span className="text-[11px] text-slate-400">Permite várias páginas no mesmo domínio. Reservados: {cfg.reserved_paths.slice(0, 8).join(", ")}…</span>
                </label>
              )}
              <button data-testid="wizard-add" disabled={busy === "add"} onClick={add} className="btn btn-primary w-full justify-center disabled:opacity-60">{busy === "add" ? <Loader2 size={15} className="animate-spin" /> : <Globe size={15} />} Conectar {pre.domain}</button>
            </>
          )}
          <p className="text-[11px] text-slate-400">Funciona com domínio raiz e subdomínio. Depois de criar os registros, o certificado SSL é emitido automaticamente.</p>
        </>
      )}
    </div>
  );
};
